import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  Upload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Edit as EditIcon,
  Psychology as PsychologyIcon,
  Architecture as ArchitectureIcon,
  Science as ScienceIcon,
  MilitaryTech as MilitaryTechIcon,
  Lightbulb as LightbulbIcon,
  VolunteerActivism as VolunteerActivismIcon,
  Palette as PaletteIcon,
  Campaign as CampaignIcon,
  EmojiEvents as EmojiEventsIcon,
  Inventory as InventoryIcon,
  Shield as ShieldIcon,
  BusinessCenter as BusinessCenterIcon,
  Handshake as HandshakeIcon,
  Build as BuildIcon,
  Explore as ExploreIcon,
  RocketLaunch as RocketLaunchIcon,
  MusicNote as MusicNoteIcon,
} from "@mui/icons-material";

type PersonalityType =
  | "INTJ"
  | "INTP"
  | "ENTJ"
  | "ENTP"
  | "INFJ"
  | "INFP"
  | "ENFJ"
  | "ENFP"
  | "ISTJ"
  | "ISFJ"
  | "ESTJ"
  | "ESFJ"
  | "ISTP"
  | "ISFP"
  | "ESTP"
  | "ESFP";

interface PersonalityTestResult {
  id: number;
  personality_type: PersonalityType;
  summary: string | null;
  test_date: string;
  pdf_file: File | null;
}

interface PersonalityTestTabProps {
  personalityTest: PersonalityTestResult | null;
  setPersonalityTest: React.Dispatch<React.SetStateAction<PersonalityTestResult | null>>;
}

const PERSONALITY_CATEGORIES: Record<string, { name: string; color: string; textColor: string }> = {
  Analysts: { name: "Analysts", color: "#88619a", textColor: "#ffffff" },
  Diplomats: { name: "Diplomats", color: "#33a474", textColor: "#ffffff" },
  Sentinels: { name: "Sentinels", color: "#4298b4", textColor: "#ffffff" },
  Explorers: { name: "Explorers", color: "#d8a537", textColor: "#000000" },
};

const PERSONALITY_TYPES: Record<
  PersonalityType,
  { name: string; description: string; icon: React.ElementType; category: string }
> = {
  INTJ: { name: "The Architect", description: "Imaginative and strategic thinkers", icon: ArchitectureIcon, category: "Analysts" },
  INTP: { name: "The Logician", description: "Innovative inventors with a thirst for knowledge", icon: ScienceIcon, category: "Analysts" },
  ENTJ: { name: "The Commander", description: "Bold, imaginative and strong-willed leaders", icon: MilitaryTechIcon, category: "Analysts" },
  ENTP: { name: "The Debater", description: "Smart and curious thinkers who cannot resist an intellectual challenge", icon: LightbulbIcon, category: "Analysts" },
  INFJ: { name: "The Advocate", description: "Quiet and mystical, yet very inspiring and tireless idealists", icon: VolunteerActivismIcon, category: "Diplomats" },
  INFP: { name: "The Mediator", description: "Poetic, kind and altruistic, always eager to help a good cause", icon: PaletteIcon, category: "Diplomats" },
  ENFJ: { name: "The Protagonist", description: "Charismatic and inspiring leaders, able to mesmerize their listeners", icon: CampaignIcon, category: "Diplomats" },
  ENFP: { name: "The Campaigner", description: "Enthusiastic, creative and sociable free spirits", icon: EmojiEventsIcon, category: "Diplomats" },
  ISTJ: { name: "The Logistician", description: "Practical and fact-minded individuals, whose reliability cannot be doubted", icon: InventoryIcon, category: "Sentinels" },
  ISFJ: { name: "The Defender", description: "Dedicated and warm protectors, always ready to defend their loved ones", icon: ShieldIcon, category: "Sentinels" },
  ESTJ: { name: "The Executive", description: "Excellent administrators, unsurpassed at managing things or people", icon: BusinessCenterIcon, category: "Sentinels" },
  ESFJ: { name: "The Consul", description: "Extraordinarily caring, social and popular people, always eager to help", icon: HandshakeIcon, category: "Sentinels" },
  ISTP: { name: "The Virtuoso", description: "Bold and practical experimenters, masters of all kinds of tools", icon: BuildIcon, category: "Explorers" },
  ISFP: { name: "The Adventurer", description: "Flexible and charming artists, always ready to explore and experience something new", icon: ExploreIcon, category: "Explorers" },
  ESTP: { name: "The Entrepreneur", description: "Smart, energetic and very perceptive people, who truly enjoy living on the edge", icon: RocketLaunchIcon, category: "Explorers" },
  ESFP: { name: "The Entertainer", description: "Spontaneous, energetic and enthusiastic people, life is never boring around them", icon: MusicNoteIcon, category: "Explorers" },
};

export default function PersonalityTestTab({
  personalityTest,
  setPersonalityTest,
}: PersonalityTestTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPersonality, setSelectedPersonality] = useState<PersonalityType>(
    personalityTest?.personality_type || "INTJ"
  );
  const [summary, setSummary] = useState<string | null>(personalityTest?.summary || null);
  const [testDate, setTestDate] = useState<string>(
    personalityTest?.test_date || new Date().toISOString().split("T")[0]
  );
  const [pdfFile, setPdfFile] = useState<File | null>(personalityTest?.pdf_file || null);

  const handleSave = () => {
    setPersonalityTest({
      id: personalityTest?.id || Date.now(),
      personality_type: selectedPersonality,
      summary: summary,
      test_date: testDate,
      pdf_file: pdfFile,
    });
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setSelectedPersonality(personalityTest?.personality_type || "INTJ");
    setSummary(personalityTest?.summary || null);
    setTestDate(personalityTest?.test_date || new Date().toISOString().split("T")[0]);
    setPdfFile(personalityTest?.pdf_file || null);
    setDialogOpen(false);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setPdfFile(file);
    } else {
      alert("Please upload a PDF file");
    }
  };

  const handleDeletePdf = () => {
    setPdfFile(null);
  };

  const currentPersonality = personalityTest
    ? PERSONALITY_TYPES[personalityTest.personality_type]
    : null;

  const currentCategory = currentPersonality
    ? PERSONALITY_CATEGORIES[currentPersonality.category]
    : null;

  const SelectedIcon = selectedPersonality ? PERSONALITY_TYPES[selectedPersonality].icon : null;

  return (
    <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          16 Personalities Test
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {personalityTest ? "Edit Results" : "Add Results"}
        </Button>
      </Box>

      {personalityTest ? (
        <Card sx={{ boxShadow: 0 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
              {/* Profile Icon and Name */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 150,
                }}
              >
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    backgroundColor: currentCategory?.color || "#5D4E37",
                    mb: 1,
                  }}
                >
                  {currentPersonality?.icon && <currentPersonality.icon sx={{ fontSize: 60 }} />}
                </Avatar>
                <Typography variant="h5" fontWeight="bold" align="center">
                  {personalityTest.personality_type}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {currentPersonality?.name}
                </Typography>
                <Chip
                  label={currentCategory?.name}
                  size="small"
                  sx={{
                    mt: 1,
                    backgroundColor: currentCategory?.color || "#5D4E37",
                    color: currentCategory?.textColor || "#ffffff",
                    fontWeight: "bold",
                  }}
                />
              </Box>

              {/* Details */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {currentPersonality?.description}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Test Date
                  </Typography>
                  <Chip
                    label={new Date(personalityTest.test_date).toLocaleDateString()}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {personalityTest.summary && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Summary
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      <Typography variant="body2" whiteSpace="pre-wrap">
                        {personalityTest.summary}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {personalityTest.pdf_file && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Test Results PDF
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <PdfIcon sx={{ fontSize: 32, color: "#f44336" }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {personalityTest.pdf_file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(personalityTest.pdf_file.size / 1024).toFixed(2)} KB
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const url = URL.createObjectURL(personalityTest.pdf_file!);
                          window.open(url, "_blank");
                        }}
                      >
                        View PDF
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ boxShadow: 0 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                gap: 2,
              }}
            >
              <PsychologyIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                No Personality Test Results
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Add your 16 Personalities test results to see your profile type and
                characteristics
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {personalityTest ? "Edit Personality Test Results" : "Add Personality Test Results"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Personality Type Selection */}
            <FormControl fullWidth>
              <InputLabel>Select Personality Type</InputLabel>
              <Select
                value={selectedPersonality}
                label="Select Personality Type"
                onChange={(e) => setSelectedPersonality(e.target.value as PersonalityType)}
              >
                {Object.entries(PERSONALITY_TYPES).map(([type, info]) => (
                  <MenuItem key={type} value={type}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: PERSONALITY_CATEGORIES[info.category].color,
                        }}
                      >
                        <info.icon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {info.name}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Test Date */}
            <TextField
              fullWidth
              label="Test Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={testDate}
              onChange={(e) => setTestDate(e.target.value)}
            />

            {/* Summary Text Field */}
            <TextField
              fullWidth
              label="Summary of Results"
              multiline
              rows={4}
              placeholder="Enter your personality test summary and details here..."
              value={summary || ""}
              onChange={(e) => setSummary(e.target.value)}
              helperText="Write detailed notes about your test results, insights, and observations"
            />

            {/* PDF Upload */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload Test Results PDF
              </Typography>
              {pdfFile ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <PdfIcon sx={{ fontSize: 32, color: "#f44336" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {pdfFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(pdfFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleDeletePdf}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  Upload PDF
                  <input
                    type="file"
                    accept=".pdf"
                    hidden
                    onChange={handleFileUpload}
                  />
                </Button>
              )}
            </Box>

            {/* Preview of Selected Personality */}
            {selectedPersonality && SelectedIcon && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: PERSONALITY_CATEGORIES[PERSONALITY_TYPES[selectedPersonality].category].color,
                  }}
                >
                  <SelectedIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedPersonality} - {PERSONALITY_TYPES[selectedPersonality].name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {PERSONALITY_TYPES[selectedPersonality].description}
                  </Typography>
                  <Chip
                    label={PERSONALITY_CATEGORIES[PERSONALITY_TYPES[selectedPersonality].category].name}
                    size="small"
                    sx={{
                      mt: 0.5,
                      backgroundColor: PERSONALITY_CATEGORIES[PERSONALITY_TYPES[selectedPersonality].category].color,
                      color: PERSONALITY_CATEGORIES[PERSONALITY_TYPES[selectedPersonality].category].textColor,
                      fontWeight: "bold",
                      height: 20,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
